import type { NormalizedRoundSchedule, RoundAssignment, RoundGroup } from "../round/types";
import type { AssignFromPoolOperation, RoundOperation } from "./operations";

export type OperationResult = {
  schedule: NormalizedRoundSchedule;
  inverse: RoundOperation | RoundOperation[];
  affectsTransport: boolean;
};

function cloneSchedule(schedule: NormalizedRoundSchedule): NormalizedRoundSchedule {
  return {
    ...schedule,
    groups: schedule.groups.map((group) => ({ ...group, playerNames: [...group.playerNames] })),
    assignments: schedule.assignments.map((assignment) => ({ ...assignment })),
    people: schedule.people.map((person) => ({ ...person, assignmentIds: [...person.assignmentIds] })),
    sessionModel: schedule.sessionModel.kind === "weekend-single-session"
      ? {
          kind: "weekend-single-session",
          session: { ...schedule.sessionModel.session, groupIds: [...schedule.sessionModel.session.groupIds] },
        }
      : {
          kind: "weekday-two-session",
          sessions: schedule.sessionModel.sessions.map((session) => ({ ...session, groupIds: [...session.groupIds] })),
        },
  };
}

function holesForAssignment(assignment: RoundAssignment) {
  return assignment.segment === "full18" || assignment.segment === "extraFull18" ? 18 : 9;
}

function assignmentInterval(assignment: RoundAssignment, group: RoundGroup): [number, number] | null {
  if (assignment.segment === "full18" || assignment.segment === "extraFull18") {
    return group.finish18Minutes == null ? null : [group.startMinutes, group.finish18Minutes];
  }
  if (assignment.segment === "holes1to9" || assignment.segment === "extra1to9") {
    return group.finish9Minutes == null ? null : [group.startMinutes, group.finish9Minutes];
  }
  if (assignment.segment === "holes10to18" || assignment.segment === "extra10to18") {
    return group.finish9Minutes == null || group.finish18Minutes == null
      ? null
      : [group.finish9Minutes, group.finish18Minutes];
  }
  return null;
}

function longestBreak(intervals: Array<[number, number]>) {
  if (intervals.length < 2) return 0;
  const ordered = [...intervals].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  let activeEnd = ordered[0][1];
  let longest = 0;
  for (const [start, end] of ordered.slice(1)) {
    longest = Math.max(longest, Math.max(0, start - activeEnd));
    activeEnd = Math.max(activeEnd, end);
  }
  return longest;
}

function refreshPeople(schedule: NormalizedRoundSchedule) {
  const assignmentsByPerson = new Map<string, RoundAssignment[]>();
  const groupsById = new Map(schedule.groups.map((group) => [group.id, group]));
  for (const assignment of schedule.assignments) {
    const rows = assignmentsByPerson.get(assignment.personId) || [];
    rows.push(assignment);
    assignmentsByPerson.set(assignment.personId, rows);
  }

  schedule.people = schedule.people.map((person) => {
    const rows = assignmentsByPerson.get(person.personId) || [];
    const next = {
      ...person,
      holes: rows.reduce((sum, assignment) => sum + holesForAssignment(assignment), 0),
      assignmentIds: rows.map((assignment) => assignment.id),
    };

    if (!rows.length) {
      return {
        ...next,
        dutyStartMinutes: null,
        dutyEndMinutes: null,
        longestBreakMinutes: 0,
      };
    }

    const intervals = rows.map((assignment) => {
      const group = groupsById.get(assignment.groupId);
      return group ? assignmentInterval(assignment, group) : null;
    });
    if (intervals.some((interval) => interval === null)) return next;

    const complete = intervals as Array<[number, number]>;
    return {
      ...next,
      dutyStartMinutes: Math.min(...complete.map(([start]) => start)),
      dutyEndMinutes: Math.max(...complete.map(([, end]) => end)),
      longestBreakMinutes: longestBreak(complete),
    };
  });
}

function ensurePerson(schedule: NormalizedRoundSchedule, operation: AssignFromPoolOperation) {
  if (schedule.people.some((person) => person.personId === operation.personId)) return;
  schedule.people.push({
    personId: operation.personId,
    displayName: operation.person?.displayName || operation.personId,
    holes: 0,
    dutyStartMinutes: null,
    dutyEndMinutes: null,
    longestBreakMinutes: 0,
    assignmentIds: [],
    driverEligible: Boolean(operation.person?.driverEligible),
    transportIncluded: operation.person?.transportIncluded !== false,
  });
}

function assignmentOrThrow(schedule: NormalizedRoundSchedule, assignmentId: string) {
  const assignment = schedule.assignments.find((item) => item.id === assignmentId);
  if (!assignment) throw new Error("Nie znaleziono pozycji w aktualnym workspace.");
  return assignment;
}

function ensureUnlocked(...assignments: RoundAssignment[]) {
  if (assignments.some((assignment) => assignment.locked)) throw new Error("Najpierw odblokuj tę pozycję.");
}

export function applyRoundOperation(current: NormalizedRoundSchedule, operation: RoundOperation): OperationResult {
  const schedule = cloneSchedule(current);

  if (operation.type === "move-assignment") {
    const assignment = assignmentOrThrow(schedule, operation.assignmentId);
    ensureUnlocked(assignment);
    const inverse: RoundOperation = {
      type: "move-assignment",
      assignmentId: assignment.id,
      toGroupId: assignment.groupId,
      toSegment: assignment.segment,
    };
    assignment.groupId = operation.toGroupId;
    assignment.segment = operation.toSegment;
    assignment.source = "manual";
    refreshPeople(schedule);
    return { schedule, inverse, affectsTransport: true };
  }

  if (operation.type === "swap-assignment") {
    const first = assignmentOrThrow(schedule, operation.firstAssignmentId);
    const second = assignmentOrThrow(schedule, operation.secondAssignmentId);
    ensureUnlocked(first, second);
    const firstLocation = { groupId: first.groupId, segment: first.segment };
    first.groupId = second.groupId;
    first.segment = second.segment;
    second.groupId = firstLocation.groupId;
    second.segment = firstLocation.segment;
    first.source = "manual";
    second.source = "manual";
    refreshPeople(schedule);
    return { schedule, inverse: operation, affectsTransport: true };
  }

  if (operation.type === "full-plan-swap") {
    if (operation.personAId === operation.personBId) throw new Error("Wybierz dwie różne osoby.");
    const affected = schedule.assignments.filter(
      (assignment) => assignment.personId === operation.personAId || assignment.personId === operation.personBId,
    );
    if (!affected.length) throw new Error("Nie znaleziono planu jednej z wybranych osób.");
    ensureUnlocked(...affected);
    for (const assignment of affected) {
      assignment.personId = assignment.personId === operation.personAId ? operation.personBId : operation.personAId;
      assignment.source = "manual";
    }
    refreshPeople(schedule);
    return { schedule, inverse: operation, affectsTransport: true };
  }

  if (operation.type === "assign-from-pool") {
    ensurePerson(schedule, operation);
    const assignmentId = operation.assignmentId || `manual:${crypto.randomUUID()}`;
    if (schedule.assignments.some((assignment) => assignment.id === assignmentId)) throw new Error("Pozycja o takim identyfikatorze już istnieje.");
    schedule.assignments.push({
      id: assignmentId,
      groupId: operation.groupId,
      personId: operation.personId,
      segment: operation.segment,
      source: "manual",
      locked: Boolean(operation.locked),
    });
    refreshPeople(schedule);
    return {
      schedule,
      inverse: { type: "remove-assignment", assignmentId },
      affectsTransport: true,
    };
  }

  if (operation.type === "remove-assignment") {
    const assignment = assignmentOrThrow(schedule, operation.assignmentId);
    ensureUnlocked(assignment);
    schedule.assignments = schedule.assignments.filter((item) => item.id !== assignment.id);
    refreshPeople(schedule);
    return {
      schedule,
      inverse: {
        type: "assign-from-pool",
        assignmentId: assignment.id,
        personId: assignment.personId,
        groupId: assignment.groupId,
        segment: assignment.segment,
        locked: assignment.locked,
      },
      affectsTransport: true,
    };
  }

  const assignment = assignmentOrThrow(schedule, operation.assignmentId);
  const previous = assignment.locked;
  assignment.locked = operation.locked;
  return {
    schedule,
    inverse: { type: "set-lock", assignmentId: assignment.id, locked: previous },
    affectsTransport: false,
  };
}

export function applyInverse(schedule: NormalizedRoundSchedule, inverse: RoundOperation | RoundOperation[]): NormalizedRoundSchedule {
  const operations = Array.isArray(inverse) ? inverse : [inverse];
  return operations.reduce((current, operation) => applyRoundOperation(current, operation).schedule, schedule);
}
